/**
 * Relance manager — contenu et garde-fous.
 *
 * Un encadrant peut, depuis une alerte du cockpit, envoyer un encouragement aux
 * personnes concernées. C'est la seule notification déclenchée par un humain
 * vers un autre : elle appelle donc plus de précautions que les notifications
 * automatiques.
 *
 * ── Trois garde-fous ──────────────────────────────────────────────────────────
 * 1. **Fréquence** : une relance par destinataire et par fenêtre, TOUS ÉMETTEURS
 *    CONFONDUS. Sans ça, un manager et un organisateur pourraient relancer la
 *    même personne le même jour sans le savoir.
 * 2. **Périmètre** : on ne relance que des gens du périmètre de Pilot
 *    (`pilotScopeFromRequest` → `visibleIdsFor`), vérifié côté serveur ; un
 *    collaborateur de filiale est refusé (403, décision D1 du 01/10/2026).
 * 3. **Ton** : le contenu est formulé en encouragement, jamais en reproche —
 *    cohérent avec la doctrine anti-flicage arbitrée le 25/07. Le manager ne
 *    rédige pas le message : le bouton reste un clic, et personne ne peut se
 *    servir de BizCoach pour envoyer une remontrance.
 */
import type { Lang } from "./notification-defaults";

/** Fenêtre pendant laquelle une personne déjà relancée ne l'est plus. */
export const NUDGE_COOLDOWN_DAYS = 7;

/**
 * Les félicitations ont une fenêtre bien plus courte que les relances.
 * Une relance répétée harcèle ; une reconnaissance répétée fait plaisir. On
 * borde seulement le double-clic et les envois en rafale sur un même motif.
 */
export const PRAISE_COOLDOWN_HOURS = 12;

/**
 * Plafond par envoi. Une alerte porte au plus une équipe entière ; au-delà,
 * c'est probablement une erreur de périmètre plutôt qu'une intention.
 */
export const MAX_NUDGE_RECIPIENTS = 50;

export interface NudgeContent {
  title: Record<Lang, string>;
  body: Record<Lang, string>;
  emailHtml: Record<Lang, string>;
}

/** Corps du message selon l'alerte d'origine. Toujours au présent, jamais au reproche. */
const BY_SIGNAL: Record<string, { fr: string; en: string }> = {
  dropoff: {
    fr: "Reprends le fil quand tu veux : une partie de dix minutes suffit, et tes sessions t'attendent sur ton accueil.",
    en: "Pick things back up whenever you like: a ten-minute game is enough, and your sessions are waiting on your home page.",
  },
  never_started: {
    fr: "Ta première partie t'attend : dix minutes sur un cas simple, et tu vois tout de suite comment ça marche.",
    en: "Your first game is waiting: ten minutes on a simple case, and you see right away how it works.",
  },
  path_stalled: {
    fr: "Ton parcours est prêt, sa première phase est courte : lance-la, le reste s'enchaîne.",
    en: "Your path is ready and its first phase is short: start it, the rest follows.",
  },
  no_path: {
    fr: "Tu t'entraînes régulièrement — un parcours te donnerait un cap et une progression mesurable.",
    en: "You train regularly — a learning path would give you direction and measurable progress.",
  },
  // Plan d'entraînement — ton coach : on nomme ce qu'on veut obtenir.
  plan_week: {
    fr: "Ta session de la semaine t'attend dans « Mes sessions ». Une partie suffit pour rester dans le rythme de la campagne.",
    en: "Your session of the week is waiting in “My sessions”. One game keeps you in the campaign's rhythm.",
  },
  campaign_low_participation: {
    fr: "L'équipe est lancée sur la campagne. Ta première session, même courte, te met dans le mouvement — et ta compétence commence à monter.",
    en: "The team is up and running on the campaign. Your first session, even a short one, gets you in the flow — and your skill starts climbing.",
  },
  week_missed: {
    fr: "Reprends le fil de la campagne cette semaine : une seule session suffit, et la marche est petite.",
    en: "Pick the campaign back up this week: a single session is enough, and the step is a small one.",
  },
  campaign_ending: {
    fr: "La campagne se termine dans quelques jours : une dernière session fixe ton niveau pour le bilan de l'équipe.",
    en: "The campaign ends in a few days: one last session sets your level for the team's results.",
  },
};

const FALLBACK = {
  fr: "Ton manager t'invite à reprendre l'entraînement quand tu auras un moment.",
  en: "Your manager invites you to get back to training when you have a moment.",
};

/**
 * Registre du message (français) : « tu » par défaut, « vous » si le manager le
 * préfère — c'est SA parole. L'anglais n'a pas la question.
 */
export type MessageRegister = "tu" | "vous";
export const MESSAGE_REGISTER_KEY = "bizcoach_message_register";

/**
 * Registre de l'ENTREPRISE (décision 6.2) : défaut de l'aperçu des relances, félicitations et
 * invitations. Tenu EN MÉMOIRE, alimenté par ce que le serveur renvoie (`/api/auth/me` pour son
 * compte, la barre de périmètre de Pilot pour le compte piloté, l'enregistrement de la fiche Société).
 * La copie du navigateur n'est plus qu'un secours : quand elle était bloquée (navigation privée,
 * stockage refusé), l'aperçu retombait sur « tu » même si l'entreprise avait choisi « vous »
 * (lot 5, 29/09/2026). Le manager peut toujours le changer pour UN message dans l'aperçu.
 */
let companyRegister: MessageRegister | null = null;
export function setCompanyMessageRegister(r: string | null | undefined): void {
  companyRegister = r === "vous" ? "vous" : "tu";
  try { localStorage.setItem(MESSAGE_REGISTER_KEY, companyRegister); } catch { /* secours seulement */ }
}
export function companyMessageRegister(): MessageRegister {
  if (companyRegister) return companyRegister;
  try { return localStorage.getItem(MESSAGE_REGISTER_KEY) === "vous" ? "vous" : "tu"; } catch { return "tu"; }
}

const BY_SIGNAL_VOUS: Record<string, string> = {
  dropoff: "Reprenez le fil quand vous voulez : une partie de dix minutes suffit, et vos sessions vous attendent sur votre accueil.",
  never_started: "Votre première partie vous attend : dix minutes sur un cas simple, et vous voyez tout de suite comment ça marche.",
  path_stalled: "Votre parcours est prêt, sa première phase est courte : lancez-la, le reste s'enchaîne.",
  no_path: "Vous vous entraînez régulièrement — un parcours vous donnerait un cap et une progression mesurable.",
  plan_week: "Votre session de la semaine vous attend dans « Mes sessions ». Une partie suffit pour rester dans le rythme de la campagne.",
  campaign_low_participation: "L'équipe est lancée sur la campagne. Votre première session, même courte, vous met dans le mouvement — et votre compétence commence à monter.",
  week_missed: "Reprenez le fil de la campagne cette semaine : une seule session suffit, et la marche est petite.",
  campaign_ending: "La campagne se termine dans quelques jours : une dernière session fixe votre niveau pour le bilan de l'équipe.",
};
const FALLBACK_VOUS = "Votre manager vous invite à reprendre l'entraînement quand vous aurez un moment.";
const PRAISE_VOUS: Record<string, string> = {
  progress: "Votre progression n'est pas passée inaperçue. Continuez comme ça.",
  certification: "Bravo pour votre certification — c'est une vraie étape de franchie.",
  streak: "Votre régularité force le respect. C'est elle qui fait la différence sur la durée.",
  first_session: "Première session terminée — le plus dur est fait. Bravo pour le démarrage.",
  generic: "Beau travail. C'est noté, et c'est apprécié.",
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Construit le contenu bilingue d'une relance.
 * @param senderName nom de l'encadrant — dire QUI encourage change tout.
 */
export function buildNudgeContent(senderName: string, signalKey?: string | null, register: MessageRegister = "tu"): NudgeContent {
  const base = (signalKey && BY_SIGNAL[signalKey]) || FALLBACK;
  const message = register === "vous" ? { fr: (signalKey && BY_SIGNAL_VOUS[signalKey]) || FALLBACK_VOUS, en: base.en } : base;
  // Émetteur sans nom : « Votre manager » au « vous », « Ton manager » au « tu » (décision 6.2 —
  // la version « vous » disait « Ton manager vous… »).
  const who = senderName.trim() || (register === "vous" ? "Votre manager" : "Ton manager");
  const whoEn = senderName.trim() || "Your manager";

  const title = {
    fr: register === "vous" ? `${who} vous encourage à reprendre l'entraînement` : `${who} t'encourage à reprendre l'entraînement`,
    en: `${whoEn} encourages you to get back to training`,
  };

  const html = (lang: Lang) => `
    <p style="font-size:16px;color:#384872;margin:0 0 12px">
      ${escapeHtml(lang === "fr" ? title.fr : title.en)}
    </p>
    <p style="font-size:14px;color:#556479;margin:0 0 20px;line-height:1.6">
      ${escapeHtml(lang === "fr" ? message.fr : message.en)}
    </p>
  `.trim();

  return {
    title,
    body: { fr: message.fr, en: message.en },
    emailHtml: { fr: html("fr"), en: html("en") },
  };
}

/**
 * Décision D1 de Valérie (01/10/2026) — une seule équipe dans tout Pilot : relancer ou féliciter
 * un collaborateur d'une FILIALE est refusé (403, rien n'est envoyé), jusqu'au chantier des filiales.
 *
 * Rend les destinataires demandés qui sont hors du périmètre de Pilot (`pilotVisible`, déjà borné
 * au compte par `visibleIdsFor` → `sameAccountIds`) ET dans une filiale du compte piloté
 * (`subsidiaryAccountIds`). Les autres personnes hors périmètre restent simplement ignorées
 * (`partitionNudgeTargets`), comme avant. Pur : l'appelant fournit ce qu'il a lu.
 */
export function subsidiaryRecipientIds(
  requested: readonly string[],
  pilotVisible: readonly string[],
  accountOf: ReadonlyMap<string, string | null>,
  subsidiaryAccountIds: readonly string[],
): string[] {
  const visibleSet = new Set(pilotVisible);
  const subsidiaries = new Set(subsidiaryAccountIds);
  return [...new Set(requested)].filter((id) => {
    if (visibleSet.has(id)) return false;
    const acc = accountOf.get(id);
    return !!acc && subsidiaries.has(acc);
  });
}

/**
 * Trie les destinataires demandés en trois paquets.
 * Rendu pur pour être testable sans base : l'appelant fournit ce qu'il a lu.
 *
 * @param requested     identifiants demandés par le client (jamais fiables)
 * @param visible       périmètre réel de l'encadrant
 * @param recentlyNudged personnes déjà relancées dans la fenêtre
 * @param selfId        l'encadrant lui-même, qui ne se relance pas
 */
export function partitionNudgeTargets(
  requested: string[],
  visible: string[],
  recentlyNudged: string[],
  selfId: string,
): { toNudge: string[]; skippedOutOfScope: string[]; skippedCooldown: string[] } {
  const visibleSet = new Set(visible);
  const recentSet = new Set(recentlyNudged);
  // Dédoublonne : un même id envoyé deux fois ne doit pas produire deux envois.
  const unique = [...new Set(requested)];

  const toNudge: string[] = [];
  const skippedOutOfScope: string[] = [];
  const skippedCooldown: string[] = [];

  for (const id of unique) {
    if (id === selfId || !visibleSet.has(id)) skippedOutOfScope.push(id);
    else if (recentSet.has(id)) skippedCooldown.push(id);
    else toNudge.push(id);
  }

  return { toNudge, skippedOutOfScope, skippedCooldown };
}


/** Motifs de félicitation, adossés à ce que le cockpit sait détecter. */
export type PraiseReason = "progress" | "certification" | "streak" | "first_session" | "generic";

const PRAISE_BY_REASON: Record<PraiseReason, { fr: string; en: string }> = {
  progress: {
    fr: "Ta progression n'est pas passée inaperçue. Continue comme ça.",
    en: "Your progress hasn't gone unnoticed. Keep it up.",
  },
  certification: {
    fr: "Bravo pour ta certification — c'est une vraie étape de franchie.",
    en: "Congratulations on your certification — that's a real milestone.",
  },
  streak: {
    fr: "Ta régularité force le respect. C'est elle qui fait la différence sur la durée.",
    en: "Your consistency is impressive. That's what makes the difference over time.",
  },
  first_session: {
    fr: "Première session terminée — le plus dur est fait. Bravo pour le démarrage.",
    en: "First session done — the hardest part is behind you. Great start.",
  },
  generic: {
    fr: "Beau travail. C'est noté, et c'est apprécié.",
    en: "Nice work. It's been noticed, and it's appreciated.",
  },
};

/**
 * Contenu d'une félicitation.
 *
 * Comme pour la relance, l'émetteur ne rédige pas : le geste doit rester à un
 * clic. Un champ libre transformerait un réflexe en corvée — et la
 * reconnaissance qui demande un effort de rédaction n'est jamais envoyée.
 */
export function buildPraiseContent(senderName: string, reason: PraiseReason = "generic", register: MessageRegister = "tu"): NudgeContent {
  const base = PRAISE_BY_REASON[reason] ?? PRAISE_BY_REASON.generic;
  const message = register === "vous" ? { fr: PRAISE_VOUS[reason] ?? PRAISE_VOUS.generic!, en: base.en } : base;
  const who = senderName.trim() || (register === "vous" ? "Votre manager" : "Ton manager");
  const whoEn = senderName.trim() || "Your manager";

  const title = {
    fr: register === "vous" ? `👏 ${who} vous félicite` : `👏 ${who} te félicite`,
    en: `👏 ${whoEn} says well done`,
  };

  const html = (lang: Lang) => `
    <p style="font-size:16px;color:#384872;margin:0 0 12px">
      ${escapeHtml(lang === "fr" ? title.fr : title.en)}
    </p>
    <p style="font-size:14px;color:#556479;margin:0 0 20px;line-height:1.6">
      ${escapeHtml(lang === "fr" ? message.fr : message.en)}
    </p>
  `.trim();

  return {
    title,
    body: { fr: message.fr, en: message.en },
    emailHtml: { fr: html("fr"), en: html("en") },
  };
}

/**
 * Clé du verrou qui sérialise la réservation du créneau d'une relance (ou félicitation) pour UN
 * destinataire : deux encadrants (ou deux clics) qui envoient en même temps passent l'un après
 * l'autre, et le second voit le créneau pris (audit du lot 5 : lecture puis écriture sans verrou).
 */
export function nudgeSlotLockKey(recipientId: string, kind: string): string {
  return `manager_nudge:${kind}:${recipientId}`;
}

/**
 * Compte rendu d'un envoi : `sent` = messages RÉELLEMENT remis (au moins un canal, règle
 * `wasDelivered` de l'orchestrateur) ; un créneau pris entre-temps compte comme « déjà relancé ».
 */
export function summarizeNudgeOutcomes(
  outcomes: ReadonlyArray<"delivered" | "muted" | "cooldown" | "failed">,
): { sent: number; lateCooldown: number } {
  return {
    sent: outcomes.filter((o) => o === "delivered").length,
    lateCooldown: outcomes.filter((o) => o === "cooldown").length,
  };
}

/**
 * Texte à transmettre au serveur depuis l'aperçu : seulement s'il a été RETOUCHÉ par le manager.
 * Non retouché, on n'envoie rien : le serveur garde le contenu bilingue et chaque destinataire
 * le reçoit dans SA langue (audit du lot 10 — le texte affiché, dans la langue du manager,
 * partait sinon à tous, y compris aux collaborateurs anglophones).
 */
export function messageToSend(text: string, edited: boolean): string | undefined {
  if (!edited) return undefined;
  const trimmed = text.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Applique au contenu le texte retouché par le manager, s'il y en a un : c'est alors SA parole
 * qui part, telle quelle, dans les deux langues. Sans texte retouché, le contenu bilingue est
 * rendu inchangé (langue du destinataire).
 */
export function applyManagerMessage(content: NudgeContent, rawMessage: unknown): NudgeContent {
  const custom = typeof rawMessage === "string" ? rawMessage.trim().slice(0, 300) : "";
  if (!custom) return content;
  const html = (title: string) => `<p style="font-size:16px;color:#384872;margin:0 0 12px">${escapeHtml(title)}</p><p style="font-size:14px;color:#556479;margin:0 0 20px;line-height:1.6">${escapeHtml(custom)}</p>`;
  return {
    ...content,
    body: { fr: custom, en: custom },
    emailHtml: { fr: html(content.title.fr), en: html(content.title.en) },
  };
}
